import axios from 'axios';
import { Levels } from 'Common/enums';
import type { CatchError, TemplateConfig, TemplateError, UserConfig } from '@brighterion/types/config';
import type { ScheduledDeployment } from 'Hooks/query-hooks/scheduled-deployment/useGetScheduledDeployment';
import type { ScheduledDeploymentSchema } from '@brighterion/types/scheduled-deployment';
import { parseScheduledTime } from '@brighterion/utils/scheduled-deployment';
import type { GlobalRulesOverviewOverallSchema } from '@brighterion/types/overview';

let url = '';

export const initRulesService = (config: Record<string, any>) => {
    url = config.rulesUrl;
};

export const getTemplateConfig = async () => {
    const res = await axios.get<TemplateConfig>(`${url}/config/latest`);
    return res.data;
};

export const getTemplate = async () => {
    try {
        const res = await axios.get<TemplateConfig>(`${url}/config/latest`);
        return { data: res.data };
    } catch (error) {
        const res = (error as any).response;
        return { error: (res as CatchError<TemplateError>).data };
    }
};

export const getGlobalRules = async (templateRepositoryId: number, showDeletedRule: boolean = false) => {
    const res = await axios.get<GlobalRulesData>(`${url}/rulerevisions/latest`, {
        params: {
            level: 'ACQUIRER',
            templateRepositoryId,
            inherit: true,
            showDeletedRule
        }
    });

    return res.data;
};

export const getDeployedGlobalRules = async (
    templateRepositoryId: number,
    showDeletedRule: boolean = false,
    version?: number,
    simulationId?: string
) => {
    const res = await axios.get<GlobalRulesData>(`${url}/rulerevisions/deployed`, {
        params: {
            level: 'ACQUIRER',
            templateRepositoryId,
            inherit: true,
            version,
            simulationId,
            showDeletedRule
        }
    });

    return res.data;
};

export const getDeployedLandlordRules = async () => {
    const res = await axios.get<GlobalRulesData>(`${url}/rulerevisions/landlord-deployed`);
    return res.data;
};

export const getMerchantRules = async (templateRepositoryId: number, merchantId: string) => {
    const res = await axios.get<GlobalRulesData>(`${url}/rulerevisions/latest`, {
        params: {
            level: 'MERCHANT',
            templateRepositoryId,
            inherit: true,
            merchantId
        }
    });

    return res.data;
};

export const getDeployedMerchantRules = async (templateRepositoryId: number, merchantId: string, version?: number) => {
    const res = await axios.get<GlobalRulesData>(`${url}/rulerevisions/deployed`, {
        params: {
            level: 'MERCHANT',
            templateRepositoryId,
            inherit: true,
            merchantId,
            version
        }
    });

    return res.data;
};

export const getDeployedTenantGroupOverrides = async (
    templateRepositoryId: number,
    tenantGroupId: string,
    version?: number
) => {
    const res = await axios.get<GlobalRulesData>(`${url}/rulerevisions/deployed`, {
        params: {
            level: Levels.TENANT_GROUP,
            templateRepositoryId,
            inherit: true,
            tenantGroupId,
            version
        }
    });

    return res.data;
};

export const patchMerchantRules = (
    templateRepositoryId: number,
    merchantId: string,
    data: RecursivePartial<GlobalRulesData>
) => {
    return axios.patch(`${url}/rulerevisions`, {
        templateRepositoryId,
        level: 'MERCHANT',
        merchantId,
        ...data
    });
};

export const deleteMerchantRules = (templateRepositoryId: number, merchantId: string, data: { ruleIds: string[] }) => {
    return axios.delete(`${url}/rulerevisions`, {
        data: {
            templateRepositoryId,
            level: 'MERCHANT',
            merchantId,
            ...data
        }
    });
};

export const patchGlobalRules = (data: RecursivePartial<GlobalRulesData>) => {
    return axios.patch(`${url}/rulerevisions`, {
        level: 'ACQUIRER',
        ...data
    });
};

export const patchMerchantOverview = (data: RecursivePartial<GlobalRulesData>) => {
    return axios.patch(`${url}/rulerevisions`, {
        level: 'MERCHANT',
        ...data
    });
};

export const postMerchantOverview = (data: RecursivePartial<NewMerchant>) => {
    return axios.post(`${url}/merchants`, {
        ...data
    });
};

export const postDeploy = async (data: DeployPost): Promise<DeploymentResponse> => {
    const res = await axios.post(`${url}/deployment`, data);
    return res.data;
};

export const getMerchantOverrides = (templateRepositoryId: number) => {
    return axios.get<MerchantOverrideDataItem[]>(`${url}/overviewData`, {
        params: {
            level: 'MERCHANT',
            templateRepositoryId
        }
    });
};

export const getDeployedMerchantOverrides = (templateRepositoryId: number, version?: number) => {
    return axios.get<MerchantOverrideDataItem[]>(`${url}/overviewData/deployed`, {
        params: {
            level: 'MERCHANT',
            templateRepositoryId,
            version
        }
    });
};

export const getAllDeployments = async (templateRepositoryId: number) => {
    const res = await axios.get<Deployment[]>(`${url}/deployment`, {
        params: {
            templateRepositoryId
        }
    });

    return res.data;
};

export const getSingleDeployment = async (templateRepositoryId: number, versionNumber: number) => {
    const res = await axios.get<Deployment>(`${url}/deployment/${versionNumber}`, {
        params: {
            templateRepositoryId
        }
    });

    return res.data;
};

export const getLatestDeployed = async (templateRepositoryId: number) => {
    const res = await axios.get<LatestDeployment>(`${url}/deployment/latest`, {
        params: {
            templateRepositoryId
        }
    });

    return res.data;
};

export const getDeploymentSummary = async (templateRepositoryId: number) => {
    const res = await axios.get<DeploymentSummary>(`${url}/deployment/summary`, {
        params: {
            templateRepositoryId
        }
    });

    return res.data;
};

export const getScheduledDeploymentSummary = async (templateRepositoryId: number) => {
    const res = await axios.get<DeploymentSummary>(`${url}/deployment/summary`, {
        params: {
            templateRepositoryId
        }
    });
    return res.data;
};

export const getScheduledDeploymentDetails = async () => {
    const res = await axios.get<ScheduledDeploymentDetails>(`${url}/deployment/schedule/details`);
    return res.data;
};

export const deleteMerchant = async (merchantId: string, templateRepositoryId: number) => {
    return axios.delete(`${url}/merchants/${merchantId}`, {
        params: {
            templateRepositoryId
        }
    });
};

export const getLatestDeployProcess = async (
    templateRepositoryId: number,
    featureFlags?: UserConfig['featureFlags']
) => {
    // TODO: Once deployment pipeline works properly, remove this mock
    // use this condition to avoid circular depenency instead of shouldShowDeploy()
    if (featureFlags?.demo) {
        return {
            id: 4096,
            tenant: 'MASTERCARD',
            templateRepositoryId: 510,
            createdBy: 'daniel.lee2@mastercard.com',
            createdAt: '2022-08-09T16:13:26.015Z',
            lastModified: '2022-08-09T16:13:33.204Z',
            status: 'complete',
            details: {
                simulationId: 'f4d90186-c652-46eb-9c2f-032612f418db',
                totalUpdates: 8,
                groupsSummary: {
                    addedGroups: 0,
                    removedGroups: 0
                },
                acquirerSummary: {
                    addedRules: 0,
                    removedRules: 0,
                    configUpdated: false,
                    modifiedRules: 3
                },
                deployedVersion: 1,
                merchantSummary: {
                    added: 3,
                    removed: 0,
                    disabled: 0,
                    modified: 2
                },
                tenantGroupSummary: {
                    added: 3,
                    modified: 0,
                    removed: 0
                }
            }
        };
    }

    const res = await axios.get<DeploymentProcess>(`${url}/deployment/process/latest`, {
        params: {
            templateRepositoryId
        }
    });

    return res.data;
};

export const patchMerchantActiveInactive = async (
    merchantId: string,
    templateRepositoryId: number,
    merchantEnabled: boolean
) => {
    return axios.patch(`${url}/rulerevisions`, {
        templateRepositoryId,
        level: 'MERCHANT',
        merchantId,
        config: {
            merchantEnabled
        }
    });
};

export const getRulesHistory = async (params: HistoryRulesParams) => {
    const res = await axios.get<HistoryRulesResponse[]>(`${url}/history`, {
        params
    });
    return res.data;
};

export const getRulesHistoryCount = async (params: HistoryRulesParams) => {
    const res = await axios.get<HistoryRulesCount>(`${url}/history/count`, {
        params
    });
    return res.data;
};

export const getAllUsers = async () => {
    const res = await axios.get(`${url}/users`);
    return res.data;
};

export const getSingleReportCSV = (rid: number) => {
    return axios.get(`${url}/reports/csv/${rid}`, {
        responseType: 'blob'
    });
};

export const getAllRuleGroups = async () => {
    const res = await axios.get<RuleGroups>(`${url}/groups`);
    return res.data;
};

export const deleteGroup = async (id: number, templateRepositoryId: number) => {
    const res = await axios.delete<RuleGroups>(`${url}/groups/${id}`, {
        params: {
            templateRepositoryId,
            level: 'ACQUIRER'
        }
    });
    return res.data;
};

export const updateRuleGroup = async (adhocGroupId: number, updatedGroup: string, templateRepositoryId: number) => {
    const res = await axios.patch<RuleGroups>(`${url}/groups/${adhocGroupId}`, {
        newName: updatedGroup,
        level: 'ACQUIRER',
        templateRepositoryId
    });
    return res.data;
};

export const getRule = async (ruleId: string, templateRepositoryId: number) => {
    const res = await axios.get<GetRuleAPIResponse>(`${url}/ruleRevisions/latest/${ruleId}`, {
        params: {
            level: 'ACQUIRER',
            templateRepositoryId,
            inherit: true
        }
    });
    return res.data;
};

export const postNewRule = (newRule: AddNewRuleSchema) => {
    return axios.post(`${url}/rule`, newRule);
};

export const updateRule = (ruleId: number, newRule: AddNewRuleSchema) => {
    return axios.put(`${url}/rule/${ruleId}`, newRule);
};
export const deleteRule = (data: DeleteRule) => {
    return axios.delete(`${url}/rule/${data.ruleId}`, {
        params: {
            comment: data?.comment || ''
        }
    });
};

export const createGroup = (groups: Group[]) => {
    return axios.put(`${url}/groups`, groups);
};

export const getRevisionMerchantOverrides = async (templateRepositoryId: number) => {
    const res = await axios.get<RevisionMerchantOverrides>(`${url}/ruleRevisions/overrides`, {
        params: {
            templateRepositoryId
        }
    });
    return res.data;
};

export const postRuleOverrides = (data: RuleOverride) => {
    return axios.post(`${url}/ruleOverrides`, {
        ...data
    });
};

export const putRuleOverrides = (data: RuleOverride) => {
    return axios.put(`${url}/ruleOverrides`, {
        ...data
    });
};

export const deleteRuleOverrides = (ruleId: string) => {
    return axios.delete(`${url}/ruleOverrides/${ruleId}`);
};

export const getListForConclusionFlags = async () => {
    const res = await axios.get(`${url}/additionalConclusions`);
    return res?.data;
};

export const postScheduledDeployment = async (data: ScheduledDeploymentSchema): Promise<ScheduledDeployment> => {
    const parsedData = parseScheduledTime(data);
    const res = await axios.post(`${url}/deployment/schedule`, parsedData);
    return res.data;
};

export const putScheduledDeployment = async (data: ScheduledDeploymentSchema): Promise<ScheduledDeployment> => {
    const parsedData = parseScheduledTime(data);
    const res = await axios.put(`${url}/deployment/schedule`, parsedData);
    return res.data;
};

export const getScheduledDeployment = async (): Promise<ScheduledDeployment> => {
    const res = await axios.get(`${url}/deployment/schedule`);
    return res.data;
};

export const deleteScheduledDeployment = async (): Promise<ScheduledDeployment> => {
    const res = await axios.delete(`${url}/deployment/schedule`);
    return res.data;
};

export const acknowledgeScheduledDeployment = async (): Promise<ScheduledDeployment> => {
    const res = await axios.post(`${url}/deployment/schedule/acknowledge`);
    return res.data;
};

export const updateOverallThreshold = async (
    templateRepositoryId: number,
    level: Level,
    data: GlobalRulesOverviewOverallSchema
) => {
    const res = await axios.put(`${url}/config/overallThreshold`, data, {
        params: {
            level,
            templateRepositoryId
        }
    });
    return res.data;
};
